import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ModuleHeader, EmptyState } from '@/components/ui/module-header';
import { PageToolbar, FilterChip } from '@/components/ui/page-toolbar';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Combobox } from '@/components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FloatingField } from '@/components/ui/floating-field';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, apiBlob, apiForm } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import {
  DOC_ACCEPT,
  DOC_MAX_BYTES,
  validateDocumentFileClient,
} from '@/lib/documentUploadRules';
import { DocumentUploadProgress } from '@/components/DocumentUploadProgress';

const DOC_TYPES = [
  { value: 'rate_con', label: 'Rate Confirmation' },
  { value: 'bol', label: 'BOL (Bill of Lading)' },
  { value: 'pod', label: 'POD (Proof of Delivery)' },
  { value: 'fuel_receipt', label: 'Fuel Receipt' },
  { value: 'lumper', label: 'Lumper Receipt' },
  { value: 'other', label: 'Other' },
];

async function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}

export default function DocumentsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canMutateDocs =
    user?.role === 'admin' || user?.role === 'dispatcher' || user?.role === 'accountant';
  const [filter, setFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [deleteId, setDeleteId] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [form, setForm] = useState({
    load: '',
    type: 'pod',
    file: null,
    jurisdiction: 'TX',
    gallons: '',
    amountDollars: '',
  });

  const dataQ = useQuery({
    queryKey: ['documents-page', filter],
    queryFn: async () => {
      const q = filter === 'all' ? '' : `?kind=${encodeURIComponent(filter)}`;
      const [d, l] = await Promise.all([
        api(`/api/v1/documents${q}`),
        api('/api/v1/loads'),
      ]);
      if (d.status !== 200) throw new Error(d.data?.error ?? 'Failed to load documents');
      if (l.status !== 200) throw new Error(l.data?.error ?? 'Failed to load loads');
      return {
        documents: d.data.documents ?? [],
        loads: l.data.loads ?? [],
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load documents');
  }, [dataQ.isError, dataQ.error]);

  const docs = dataQ.data?.documents ?? [];
  const loads = dataQ.data?.loads ?? [];
  const deleteTarget = docs.find((d) => d.id === deleteId);

  const loadMap = useMemo(() => {
    const m = new Map();
    for (const l of loads) m.set(l.id, l.loadNumber);
    return m;
  }, [loads]);

  const onPickFile = (file) => {
    if (uploading) return;
    if (!file) {
      setForm((f) => ({ ...f, file: null }));
      return;
    }
    const check = validateDocumentFileClient(file);
    if (!check.ok) {
      toast.error(check.error);
      setForm((f) => ({ ...f, file: null }));
      return;
    }
    setForm((f) => ({ ...f, file }));
  };

  const handleUpload = async () => {
    if (uploading) return;
    if (!form.load || !form.type || !form.file) {
      toast.error('Load, type, and file are required');
      return;
    }
    const check = validateDocumentFileClient(form.file);
    if (!check.ok) {
      toast.error(check.error);
      return;
    }
    if (form.type === 'fuel_receipt') {
      const g = Number(form.gallons);
      if (!form.jurisdiction.trim() || !Number.isFinite(g) || g <= 0) {
        toast.error('Fuel receipt needs jurisdiction and gallons');
        return;
      }
    }
    setUploading(true);
    try {
      const contentBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsDataURL(form.file);
      });
      const body = {
        fileName: form.file.name,
        contentType: form.file.type || 'application/octet-stream',
        contentBase64,
        loadId: form.load,
        kind: form.type,
      };
      if (form.type === 'fuel_receipt') {
        body.jurisdiction = form.jurisdiction.trim().toUpperCase();
        body.gallons = Number(form.gallons);
        if (form.amountDollars) body.amountDollars = Number(form.amountDollars);
      }
      let r = await api('/api/v1/documents/upload-json', {
        method: 'POST',
        body,
        timeoutMs: 120000,
      });
      if (r.status === 404 || r.status === 405) {
        const fd = new FormData();
        fd.append('file', form.file);
        fd.append('loadId', form.load);
        fd.append('kind', form.type);
        if (form.type === 'fuel_receipt') {
          fd.append('jurisdiction', form.jurisdiction.trim().toUpperCase());
          fd.append('gallons', form.gallons);
          if (form.amountDollars) fd.append('amountDollars', form.amountDollars);
        }
        r = await apiForm('/api/v1/documents/upload', fd, { timeoutMs: 120000 });
      }
      if (r.status === 201) {
        toast.success('Document uploaded');
        setForm({
          load: '',
          type: 'pod',
          file: null,
          jurisdiction: 'TX',
          gallons: '',
          amountDollars: '',
        });
        setShowUpload(false);
        qc.invalidateQueries({ queryKey: ['documents-page'] });
      } else {
        toast.error(String(r.data?.error ?? 'Upload failed'));
      }
    } catch (err) {
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (id) => {
    if (uploading || removingId || downloadingId) return;
    setRemovingId(id);
    try {
      const r = await api(`/api/v1/documents/${id}`, { method: 'DELETE' });
      if (r.status === 200) {
        toast.success('Document removed');
        qc.invalidateQueries({ queryKey: ['documents-page'] });
      } else {
        toast.error(String(r.data?.error ?? 'Delete failed'));
      }
    } finally {
      setRemovingId(null);
    }
  };

  const handleDownload = async (id, fileName) => {
    if (uploading || downloadingId || removingId) return;
    setDownloadingId(id);
    try {
      const r = await apiBlob(`/api/v1/documents/${id}/download`, { timeoutMs: 120000 });
      if (r.status === 200 && r.blob) {
        await triggerBlobDownload(r.blob, r.fileName || fileName || 'document');
        return;
      }
      // Fallback: signed URL (older API / storage)
      const signed = await api(`/api/v1/documents/${id}/download-url`);
      if (signed.status === 200 && signed.data?.url) {
        const a = document.createElement('a');
        a.href = signed.data.url;
        a.download = signed.data.fileName || fileName || 'document';
        a.rel = 'noopener';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      toast.error(String(r.data?.error || signed.data?.error || 'Could not download file'));
    } catch (err) {
      toast.error(err?.message ?? 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      key: 'loadId',
      label: 'Load',
      className: 'font-mono',
      render: (v) => loadMap.get(v) ?? (v ? String(v).slice(0, 8) : '—'),
    },
    {
      key: 'kind',
      label: 'Type',
      render: (v) => <StatusPill status={v || 'other'} />,
    },
    { key: 'fileName', label: 'File', className: 'font-mono text-xs' },
    {
      key: 'sizeBytes',
      label: 'Size',
      className: 'font-mono',
      render: (v) => (v != null ? `${Math.round(v / 1024)} KB` : '—'),
    },
    {
      key: 'createdAt',
      label: 'Uploaded',
      render: (v) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" asChild>
            <Link to={`/loads?packet=${encodeURIComponent(row.loadId)}`}>
              <Icons.Paperclip className="h-3 w-3 mr-1" />
              Packet
            </Link>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={Boolean(uploading || downloadingId || removingId)}
            onClick={() => handleDownload(row.id, row.fileName)}
          >
            {downloadingId === row.id ? (
              <Icons.Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Icons.Download className="h-3 w-3" />
            )}
          </Button>
          {canMutateDocs ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={Boolean(uploading || downloadingId || removingId)}
              onClick={() => setDeleteId(row.id)}
            >
              {removingId === row.id ? (
                <Icons.Loader2 className="h-3 w-3 animate-spin text-destructive" />
              ) : (
                <Icons.Trash2 className="h-3 w-3 text-destructive" />
              )}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Documents"
        description="Load documents, BOLs, PODs, and receipts"
        icon="FolderOpen"
        metrics={[{ label: 'Total', value: docs.length }]}
        actions={
          canMutateDocs ? (
            <Button onClick={() => setShowUpload(!showUpload)} disabled={uploading}>
              <Icons.Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          ) : null
        }
      />

      {showUpload && canMutateDocs && (
        <Card aria-busy={uploading}>
          <CardHeader>
            <CardTitle>Upload Document</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DocumentUploadProgress active={uploading} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Load *
                </label>
                  <Combobox
                    options={loads.map((l) => ({
                      value: l.id,
                      label: l.loadNumber || l.id.slice(0, 8),
                    }))}
                    value={form.load}
                    onChange={(v) => setForm({ ...form, load: v })}
                    placeholder="Select load"
                    disabled={uploading}
                  />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Document Type *
                </label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}
                  disabled={uploading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.type === 'fuel_receipt' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FloatingField
                  label="Jurisdiction"
                  value={form.jurisdiction}
                  onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                  disabled={uploading}
                />
                <FloatingField
                  label="Gallons"
                  type="number"
                  value={form.gallons}
                  onChange={(e) => setForm({ ...form, gallons: e.target.value })}
                  disabled={uploading}
                />
                <FloatingField
                  label="Amount ($)"
                  type="number"
                  step="0.01"
                  value={form.amountDollars}
                  onChange={(e) => setForm({ ...form, amountDollars: e.target.value })}
                  disabled={uploading}
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                File * (PDF, PNG, JPG — max {DOC_MAX_BYTES / (1024 * 1024)}MB)
              </label>
              <div
                className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  uploading
                    ? 'opacity-60 pointer-events-none cursor-not-allowed'
                    : 'hover:border-primary cursor-pointer'
                }`}
                onClick={() => {
                  if (!uploading) document.getElementById('doc-file')?.click();
                }}
                onKeyDown={(e) => {
                  if (uploading) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    document.getElementById('doc-file')?.click();
                  }
                }}
                role="button"
                tabIndex={uploading ? -1 : 0}
                aria-disabled={uploading}
              >
                <Icons.UploadCloud className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <input
                  type="file"
                  accept={DOC_ACCEPT}
                  onChange={(e) => {
                    onPickFile(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="doc-file"
                  disabled={uploading}
                  onClick={(e) => e.stopPropagation()}
                />
                <p className="text-sm">
                  {uploading
                    ? 'Upload in progress…'
                    : form.file
                      ? form.file.name
                      : 'Click to select PDF, PNG, or JPG'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowUpload(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
              {uploading ? (
                <Button disabled className="min-w-[140px]">
                  <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading…
                </Button>
              ) : (
                <Button onClick={handleUpload} disabled={!form.load || !form.type || !form.file}>
                  Upload
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <PageToolbar>
        {['all', ...DOC_TYPES.map((t) => t.value)].map((f) => (
          <FilterChip key={f} active={filter === f} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : DOC_TYPES.find((t) => t.value === f)?.label || f}
          </FilterChip>
        ))}
      </PageToolbar>

      {docs.length > 0 ? (
        <DataTable columns={columns} data={docs} />
      ) : (
        <EmptyState
          icon="FolderOpen"
          title="No documents"
          description={
            dataQ.isLoading
              ? 'Loading…'
              : 'Upload documents from load packets to organize them here'
          }
          action={
            canMutateDocs ? (
              <Button onClick={() => setShowUpload(true)} disabled={uploading}>
                <Icons.Upload className="h-4 w-4 mr-2" />
                Upload Documents
              </Button>
            ) : null
          }
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteId)}
        onOpenChange={(open) => !open && setDeleteId('')}
        title="Delete document?"
        description={`Permanently remove ${deleteTarget?.fileName || 'this document'}?`}
        confirmLabel={removingId ? 'Deleting…' : 'Delete'}
        variant="destructive"
        onConfirm={() => {
          if (deleteId) void handleRemove(deleteId);
          setDeleteId('');
        }}
      />
    </div>
  );
}
